import { useState, useEffect } from 'react';
import { Building2, Users, Trash2, Edit2, Plus, Check, X, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Workspace, WorkspaceMember } from '../../types';
import { getWorkspaceMembers, updateWorkspace } from '../../services/firestore';

export function WorkspaceManagement(): JSX.Element {
  const { workspaces, currentWorkspace, createWorkspace, switchWorkspace } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<string | null>(null);
  const [workspaceMembers, setWorkspaceMembers] = useState<{ [key: string]: WorkspaceMember[] }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    slug: string;
    plan: Workspace['plan'];
  }>({
    name: '',
    slug: '',
    plan: 'free'
  });

  useEffect(() => {
    const loadMembers = async () => {
      const membersData: { [key: string]: WorkspaceMember[] } = {};
      for (const workspace of workspaces) {
        const members = await getWorkspaceMembers(workspace.id);
        membersData[workspace.id] = members;
      }
      setWorkspaceMembers(membersData);
    };
    loadMembers();
  }, [workspaces]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (editingWorkspace) {
        // Update existing workspace
        await updateWorkspace(editingWorkspace, {
          name: formData.name,
          slug: formData.slug,
        });
        setEditingWorkspace(null);
      } else {
        // Create new workspace
        await createWorkspace({
          name: formData.name,
          slug: formData.slug,
          plan: formData.plan,
          settings: {
            currency: 'USD',
            taxRate: 0,
            branding: {
              companyName: formData.name,
              primaryColor: '#3B82F6',
              address: '',
              email: '',
              phone: '',
            }
          }
        });
      }
      setShowCreateForm(false);
      setFormData({ name: '', slug: '', plan: 'free' });
    } catch (error) {
      console.error('Error managing workspace:', error);
      alert('Failed to manage workspace. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (workspace: Workspace) => {
    setEditingWorkspace(workspace.id);
    setFormData({
      name: workspace.name,
      slug: workspace.slug || '',
      plan: workspace.plan || 'free'
    });
    setShowCreateForm(true);
  };

  const cancelEditing = () => {
    setEditingWorkspace(null);
    setShowCreateForm(false);
    setFormData({ name: '', slug: '', plan: 'free' });
  };

  const getPlanIcon = (plan: Workspace['plan']) => {
    switch (plan) {
      case 'pro':
        return <CreditCard className="h-5 w-5 text-purple-600" />;
      case 'enterprise':
        return <Building2 className="h-5 w-5 text-blue-600" />;
      default:
        return <CreditCard className="h-5 w-5 text-gray-600" />;
    }
  };

  const getPlanBadgeColor = (plan: Workspace['plan']) => {
    switch (plan) {
      case 'pro':
        return 'bg-purple-100 text-purple-800';
      case 'enterprise':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workspaces</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your workspaces and their settings
          </p>
        </div>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Workspace
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Workspace</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workspace Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="My Workspace"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workspace URL
              </label>
              <div className="flex items-center">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                  freelancepay.app/
                </span>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="my-workspace"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEditing}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isLoading ? 'Saving...' : editingWorkspace ? 'Update Workspace' : 'Create Workspace'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {workspaces.map((workspace) => (
          <div
            key={workspace.id}
            className={`bg-white border rounded-lg overflow-hidden ${
              currentWorkspace?.id === workspace.id ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'
            }`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  {workspace.logo ? (
                    <img
                      src={workspace.logo}
                      alt={workspace.name}
                      className="h-12 w-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{workspace.name}</h3>
                    <p className="text-sm text-gray-500">freelancepay.app/{workspace.slug}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(workspace.plan)}`}>
                    {getPlanIcon(workspace.plan)}
                    <span className="ml-1 capitalize">{workspace.plan}</span>
                  </span>
                  {currentWorkspace?.id === workspace.id && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Check className="h-3 w-3 mr-1" />
                      Current
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-1" />
                    {workspaceMembers[workspace.id]?.length || 0} Members
                  </div>
                  <div className="text-sm text-gray-500">
                    Created {new Date(workspace.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {currentWorkspace?.id !== workspace.id && (
                    <button
                      onClick={() => switchWorkspace(workspace.id)}
                      className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm font-medium"
                    >
                      Switch to
                    </button>
                  )}
                  <button
                    onClick={() => startEditing(workspace)}
                    className="p-1.5 text-gray-400 hover:text-gray-500 rounded-md hover:bg-gray-50"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 